const TEMPLATE = "/applications/allianz-trade-new-business-application.pdf";
const ALLIANZ_DISTRIBUTION_TEMPLATE = "/applications/allianz-trade-account-distribution-table.xls";
const loadPdfLib = () => import("pdf-lib");
const loadSpreadsheetLib = () => import("xlsx");
const loadZipLib = () => import("jszip");
const money = (value) => Math.round(Number(value) || 0).toLocaleString("en-US");
const clean = (value) => value == null ? "" : String(value).trim();
const safeName = (value) => clean(value).replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

const download = (bytes, filename, type = "application/octet-stream") => {
  const url = URL.createObjectURL(new Blob([bytes], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const total = (rows, key) => rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
const terms = (row) => clean(row.currentTerms || row.recommendedTerms).replace(/^net\s*/i, "");

export async function generateAllianzTradeApplication({ client, rows = [], distributor }) {
  const { PDFDocument, StandardFonts } = await loadPdfLib();
  const response = await fetch(TEMPLATE);
  if (!response.ok) throw new Error("Allianz Trade application template could not be loaded");
  const pdf = await PDFDocument.load(await response.arrayBuffer());
  const form = pdf.getForm();
  const font = await pdf.embedFont(StandardFonts.Helvetica);

  const set = (name, value) => {
    const text = clean(value);
    if (!text) return;
    try { form.getTextField(name).setText(text); } catch (_) { /* template version may omit a field */ }
  };

  const homeCountry = clean(client.country).toLowerCase();
  const isDomestic = (row) => {
    const buyerCountry = clean(row.country).toLowerCase();
    if (!buyerCountry) return true;
    if (homeCountry) return buyerCountry === homeCountry;
    return /united states|usa|u\.s\./i.test(buyerCountry);
  };
  const domesticRows = rows.filter(isDomestic);
  const exportRows = rows.filter((row) => !isDomestic(row));

  set("Company legal name", client.name);
  set("Office address", client.address);
  set("City", client.city);
  set("State", client.state);
  set("Zip code", client.zipCode);
  set("Name", client.contactName);
  set("Email", client.email);
  set("Detailed description", client.description || client.industry);

  // AR summary: active accounts and current outstanding receivables. Historical sales/loss
  // fields remain editable when the source data does not contain an auditable value.
  set("Domestic", domesticRows.length);
  set("Export", exportRows.length);
  set("Domestic 4", money(total(domesticRows, "exposure")));
  set("Export 4", money(total(exportRows, "exposure")));
  const today = new Date();
  set("Month 3", String(today.getMonth() + 1).padStart(2, "0"));
  set("Date 3", String(today.getDate()).padStart(2, "0"));
  set("Year 3", today.getFullYear());
  set("Forecasted insurable sales 2", money(total(domesticRows, "exposure")));
  set("Forecasted insurable sales 1", money(total(exportRows, "exposure")));

  const ranked = [...rows].sort((a, b) => (Number(b.exposure) || 0) - (Number(a.exposure) || 0));
  ranked.slice(0, 10).forEach((row, index) => {
    const suffix = index === 0 ? "" : ` ${index}`;
    set(`Customer name${suffix}`, row.name);
    set(`Corporate address${suffix}`, row.address);
    set(`Phone number${suffix}`, row.phone);
    set(`Phone number ${10 + index}`, row.country);
    set(`Phone number ${20 + index}`, row.duns);
    set(`Phone number ${30 + index}`, money(row.exposure || row.recLimit));
  });

  ranked.filter((row) => Number(row.ar60Plus) > 0).slice(0, 5).forEach((row, index) => {
    const suffix = index === 0 ? "" : ` ${index}`;
    set(`Customer name / country${suffix}`, [row.name, row.country].filter(Boolean).join(" / "));
    set(`Account balance${suffix}`, money(row.exposure));
    set(`Amount 60 days +${suffix}`, money(row.ar60Plus));
    set(`Original terms of sale (net)${index === 0 ? "" : ` ${index * 2}`}`, terms(row));
  });

  set("Printed name", client.contactName || distributor?.name);
  set("Title", client.contactTitle);
  pdf.setTitle(`Allianz Trade application - ${client.name}`);
  pdf.setSubject("Auto-filled from the client's live CrediArc accounts-receivable portfolio");
  pdf.setCreator("CrediArc Distributor Portal");
  form.updateFieldAppearances(font);
  const pdfBytes = await pdf.save();

  // Allianz Trade also requires its Account Distribution Table. Populate the
  // supplied workbook from the exact same live AR snapshot as the PDF.
  const [zipModule, spreadsheetModule, distributionResponse] = await Promise.all([
    loadZipLib(),
    loadSpreadsheetLib(),
    fetch(ALLIANZ_DISTRIBUTION_TEMPLATE),
  ]);
  const JSZip = zipModule.default || zipModule;
  const XLSX = spreadsheetModule.default || spreadsheetModule;
  if (!distributionResponse.ok) throw new Error("Allianz Trade account distribution template could not be loaded");
  const workbook = XLSX.read(await distributionResponse.arrayBuffer(), {
    type: "array", cellDates: true, cellFormula: true, cellStyles: true,
  });
  const input = workbook.Sheets["Exposure Input"];
  const summary = workbook.Sheets["account distribution table"];
  if (!input || !summary) throw new Error("Allianz Trade account distribution template is invalid");

  const setCell = (sheet, address, value, type = typeof value === "number" ? "n" : "s") => {
    const previous = sheet[address] || {};
    sheet[address] = { ...previous, t: type, v: value };
    delete sheet[address].f;
    delete sheet[address].w;
  };
  const exposures = rows.map((row) => Number(row.exposure) || 0).filter((value) => value > 0);
  for (let rowIndex = 5; rowIndex <= 17159; rowIndex += 1) delete input[`A${rowIndex}`];
  exposures.forEach((value, index) => setCell(input, `A${index + 5}`, value, "n"));

  const brackets = [
    [0, 2500], [2500, 5000], [5000, 10000], [10000, 25000], [25000, 50000],
    [50000, 100000], [100000, 250000], [250000, 500000], [500000, 1000000], [1000000, Infinity],
  ];
  const distribution = brackets.map(([lower, upper], index) => {
    const values = exposures.filter((value) => index === 0 ? value <= upper : value > lower && value <= upper);
    return { count: values.length, amount: values.reduce((sum, value) => sum + value, 0) };
  });
  const totalExposure = exposures.reduce((sum, value) => sum + value, 0);
  distribution.forEach((bucket, index) => {
    const inputRow = index + 5;
    const summaryRow = index + 19;
    const share = totalExposure ? bucket.amount / totalExposure : 0;
    setCell(input, `E${inputRow}`, bucket.count, "n");
    setCell(input, `F${inputRow}`, bucket.amount, "n");
    setCell(input, `G${inputRow}`, share, "n");
    setCell(summary, `C${summaryRow}`, bucket.count, "n");
    setCell(summary, `D${summaryRow}`, bucket.amount, "n");
    setCell(summary, `E${summaryRow}`, share, "n");
  });
  setCell(input, "E4", exposures.length, "n");
  setCell(input, "F4", totalExposure, "n");
  setCell(summary, "C11", clean(client.name));
  setCell(summary, "C13", new Date(), "d");
  summary.C13.z = "mm/dd/yyyy";
  setCell(summary, "C14", "Whole portfolio — gross open receivables");
  setCell(summary, "C29", exposures.length, "n");
  setCell(summary, "D29", totalExposure, "n");
  setCell(summary, "E29", totalExposure ? 1 : 0, "n");
  workbook.Workbook = workbook.Workbook || {};
  workbook.Workbook.CalcPr = { ...(workbook.Workbook.CalcPr || {}), calcMode: "auto", fullCalcOnLoad: true, forceFullCalc: true };

  const xlsBytes = XLSX.write(workbook, { type: "array", bookType: "biff8", cellStyles: true });
  const base = safeName(client.name) || "client";
  const zip = new JSZip();
  zip.file(`allianz-trade-application-${base}.pdf`, pdfBytes);
  zip.file(`allianz-trade-account-distribution-${base}.xls`, xlsBytes);
  zip.file("README.txt", "Allianz Trade submission generated by CrediArc. Review all populated values, complete any remaining declarations, and sign the PDF before submission.");
  const packageBytes = await zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
  download(packageBytes, `allianz-trade-submission-${base}.zip`, "application/zip");
}

const wrap = (text, max = 82) => {
  const words = clean(text).split(/\s+/).filter(Boolean); const lines = []; let line = "";
  words.forEach((word) => { const next = line ? `${line} ${word}` : word; if (next.length > max && line) { lines.push(line); line = word; } else line = next; });
  if (line) lines.push(line); return lines;
};

export async function generateMarketApplication({ insurer, client, rows = [], distributor }) {
  const { PDFDocument, StandardFonts, rgb } = await loadPdfLib();
  const pdf = await PDFDocument.create();
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const navy = rgb(0.04, 0.16, 0.29), blue = rgb(0.12, 0.44, 0.91), muted = rgb(0.39, 0.45, 0.55), line = rgb(0.87, 0.91, 0.95);
  const exposure = total(rows, "exposure"), overdue = total(rows, "ar60Plus");
  const ranked = [...rows].sort((a, b) => (Number(b.exposure) || 0) - (Number(a.exposure) || 0));
  let page = pdf.addPage([612, 792]), y = 730;
  const text = (value, x, size = 10, face = regular, color = navy) => page.drawText(clean(value), { x, y, size, font: face, color });
  const rule = () => { page.drawLine({ start: { x: 46, y }, end: { x: 566, y }, thickness: 1, color: line }); y -= 18; };
  const field = (label, value) => { text(label.toUpperCase(), 46, 7.5, bold, muted); text(value || "Not available - complete before submission", 190, 10, regular, value ? navy : muted); y -= 23; };
  const nextPage = () => { page = pdf.addPage([612, 792]); y = 742; };

  page.drawRectangle({ x: 0, y: 754, width: 612, height: 38, color: navy });
  page.drawText("CREDIARC", { x: 46, y: 768, size: 12, font: bold, color: rgb(1, 1, 1) });
  text(`${insurer} - Credit Insurance Submission`, 46, 24, bold); y -= 18;
  text("Prepared from the applicant's live accounts-receivable portfolio", 46, 10, regular, muted); y -= 28; rule();
  text("Applicant", 46, 14, bold, blue); y -= 24;
  field("Legal name", client.name); field("Address", [client.address, client.city, client.state, client.zipCode, client.country].filter(Boolean).join(", "));
  field("Contact", client.contactName); field("Email", client.email); field("Phone", client.phone); field("Business", client.description || client.industry);
  y -= 5; rule(); text("Receivables portfolio", 46, 14, bold, blue); y -= 24;
  field("Outstanding AR", `$${money(exposure)}`); field("Active buyers", rows.length); field("60+ days overdue", `$${money(overdue)}`);
  field("Requested product", "Whole-turnover trade credit insurance"); field("Distributor", distributor?.name);
  y -= 6; text("Important", 46, 9, bold, muted); y -= 15;
  wrap("This is a CrediArc-generated market submission, not the insurer's proprietary application form. Values are populated only where supported by current platform data and must be reviewed and signed by the applicant before submission.", 94).forEach((lineText) => { text(lineText, 46, 8.5, regular, muted); y -= 12; });

  nextPage(); text("Buyer schedule", 46, 18, bold); y -= 28;
  const widths = [190, 78, 92, 78, 82]; const headers = ["Buyer", "Country", "Outstanding AR", "60+ overdue", "Requested limit"];
  let x = 46; headers.forEach((h, i) => { text(h, x, 8, bold, muted); x += widths[i]; }); y -= 14; rule();
  ranked.forEach((row) => {
    if (y < 72) { nextPage(); text("Buyer schedule - continued", 46, 16, bold); y -= 28; }
    x = 46;
    const values = [clean(row.name).slice(0, 30), clean(row.country).slice(0, 12), `$${money(row.exposure)}`, `$${money(row.ar60Plus)}`, `$${money(row.recLimit)}`];
    values.forEach((value, i) => { text(value, x, 8.5, regular); x += widths[i]; }); y -= 18;
    page.drawLine({ start: { x: 46, y: y + 6 }, end: { x: 566, y: y + 6 }, thickness: .5, color: line });
  });
  y -= 18; text("Applicant certification", 46, 13, bold, blue); y -= 24;
  wrap("The applicant must review all information, complete any missing insurer-specific questions, and confirm that the submission is true and complete before it is sent to an insurer or MGA.", 90).forEach((lineText) => { text(lineText, 46, 9.5); y -= 14; });
  y -= 22; field("Printed name", client.contactName); field("Title", client.contactTitle); field("Signature", ""); field("Date", "");
  pdf.setTitle(`${insurer} credit insurance submission - ${client.name}`);
  pdf.setCreator("CrediArc Distributor Portal");
  const bytes = await pdf.save();
  download(bytes, `${safeName(insurer)}-application-${safeName(client.name) || "client"}.pdf`);
}
